import * as React from "react";
import { observer, inject } from "mobx-react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDollarSign } from "@fortawesome/free-solid-svg-icons";

import { HorseForSaleStore } from "src/stores/HorseForSaleStore";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";
import { SimpleCustomer, ExtractFriendlyName } from "src/model/Customer";
import { HorseForSaleOutcome, HorseForSaleState, HammerFallOutcome } from "src/model/HorseForSale";

import CustomerSelect from "../common/CustomerSelect";
import { CustomerLink } from "../common/CustomerLink";
import EditableCard, { EditableInputGroup, WhenEditable, EditableSubCard, WhenNotEditable } from "../common/EditableCard";
import AttachmentLogPanel from "../common/AttachmentLogPanel";
import { HammerFallOutcomeRequest } from "src/requests/HorseForSaleRequests";
import { FileUploadRequest } from "src/requests/CommonRequest";
import { successMessage, errorMessage } from "../common/Toast";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { NumberToMonetaryStringFormat } from "src/common/Methods/FileUtil";
import { FinalBidAmountWarning } from "../common/FinalBidAmountWarning";

namespace EditableAcknowledgement {
    export interface Fields {
        id: number | null;
        finalBidAmount: string;
        winningBidder: SimpleCustomer | null;
        attachmentLog: Array<FileUploadRequest>;
    }

    export const Empty = (): Fields => {
        return {
            id: null,
            finalBidAmount: "",
            winningBidder: null,
            attachmentLog: []
        };
    };

    export const FromModel = (m: HorseForSaleOutcome | null): Fields => {
        if (m === null || m.type === "WITHDRAWAL") {
            return Empty();
        }
        return {
            id: (m && m.id) || null,
            finalBidAmount: m ? NumberToMonetaryStringFormat((m as HammerFallOutcome).finalBidAmount) : "",
            winningBidder: m ? (m as HammerFallOutcome).winningBidder : null,
            attachmentLog: []
        };
    };

    export const ToRequest = (fields: Fields): HammerFallOutcomeRequest => {
        if (!fields.finalBidAmount || !fields.winningBidder) {
            throw new Error("In ToRequest with an unvalidated object");
        }
        return {
            type: "HAMMER_FALL",
            finalBidAmount: Math.floor(parseFloat(fields.finalBidAmount.replace(/,/g, "")) * 100),
            bidderId: fields.winningBidder.id
        };
    };

    export const isAnEditablePage = (currentState: HorseForSaleState): boolean => {
        switch (currentState) {
            case HorseForSaleState.APPROVED: // Not Compliant
            case HorseForSaleState.APPROVED_FOR_SALE:
            case HorseForSaleState.PROVISIONALLY_SOLD:
                return true;
            default:
                return false;
        }
    };

    export const isAnEditableAttachment = (currentState: HorseForSaleState): boolean => {
        switch (currentState) {
            case HorseForSaleState.REJECTED: // Not Compliant
            case HorseForSaleState.WITHDRAWN_EARLY:
            case HorseForSaleState.WITHDRAWN_WITHOUT_PENALTY:
            case HorseForSaleState.WITHDRAWN_WITH_PENALTY_GENERATED:
            case HorseForSaleState.WITHDRAWN_WITH_PENALTY_PENDING:
                return false;
            default:
                return true;
        }
    };
}

export interface Props {
    customerEndpoints?: CustomerEndpoints;
    horseForSaleStore?: HorseForSaleStore;
}

export interface StatePage {
    wasValidated: boolean;
    isEditable: boolean;
}

type State = StatePage & EditableAcknowledgement.Fields;

@inject("customerEndpoints")
@inject("horseForSaleStore")
@observer
export default class AcknowledgementComponent extends React.Component<Props, State> {
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            ...EditableAcknowledgement.Empty(),
            wasValidated: false,
            isEditable: false
        };
    }

    cancelEditing = () => {
        const outcome = this.props.horseForSaleStore!.horseForSale!.outcome;
        this.setState({
            ...EditableAcknowledgement.FromModel(outcome),
            wasValidated: false,
            isEditable: false
        });
    };

    editFields = () => {
        const outcome = this.props.horseForSaleStore!.horseForSale!.outcome;
        this.setState({
            ...EditableAcknowledgement.FromModel(outcome),
            isEditable: true
        });
    };

    cancelEditableFields = () => {
        this.setState({ isEditable: false });
    };

    submit = (): void => {
        if (this.state.winningBidder !== null && this.state.finalBidAmount !== "") {
            this.setState({ wasValidated: false });
            if (this.props.horseForSaleStore!.horseForSale!.outcome === null) {
                this.addWinningBidder();
            } else {
                this.editWinningBidder();
            }
        } else {
            this.setState({ wasValidated: true });
        }
    };

    addWinningBidder = async () => {
        try {
            if (!this.state.winningBidder) {
                return;
            }
            const goAhead = await SimpleConfirmation(`Are you sure you'd like to add ${ExtractFriendlyName(this.state.winningBidder)} 
                as the Winning Bidder of ${this.props.horseForSaleStore!.horseForSale!.horse.name}?`);
            if (!goAhead) {
                return;
            }

            await this.props.horseForSaleStore!.registerOutcome(this.generateRequest());
            successMessage(`
                ${ExtractFriendlyName(this.state.winningBidder)} has been added as the Winning Bidder of 
                ${this.props.horseForSaleStore!.horseForSale!.horse.name} for 
                ${"$" + NumberToMonetaryStringFormat(parseInt(this.state.finalBidAmount, 10) * 100)}.`);
            this.cancelEditing();
        } catch (error) {
            errorMessage(error);
        }
    };

    editWinningBidder = async () => {
        try {
            if (!this.state.winningBidder) {
                return;
            }
            const goAhead = await SimpleConfirmation(`Are you sure you'd like to update the form's details?`);
            if (!goAhead) {
                return;
            }
            await this.props.horseForSaleStore!.updateOutcome(this.generateRequest());
            successMessage(`Changes successfully saved.`);
            this.cancelEditing();
        } catch (error) {
            errorMessage(error);
        }
    };

    generateRequest = (): HammerFallOutcomeRequest => {
        return EditableAcknowledgement.ToRequest(this.state);
    };

    handleOnChange = (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>
            | React.ChangeEvent<HTMLSelectElement>
    ): void => {
        event.preventDefault();
        this.setState({ [event.target.name]: event.target.value } as any);
    };

    render() {
        const { horseForSaleStore } = this.props;
        if (!horseForSaleStore) {
            throw new Error(" ** No Horse For Sale Store **");
        }

        const horseForSale = horseForSaleStore.horseForSale;
        if (!horseForSale) {
            return <div />;
        }

        const pageTitle = "Acknowledgement of Purchase"; //  and Acceptance of Conditions Form
        const withdrawn = horseForSale.outcome && horseForSale.outcome.type === "WITHDRAWAL";

        return (
            <>
                <EditableCard
                    title={pageTitle}
                    editable={this.state.isEditable}
                    onEdit={this.editFields}
                    onCancel={this.cancelEditing}
                    onSave={this.submit}
                    showButtons={EditableAcknowledgement.isAnEditablePage(horseForSale.state) && horseForSale.hipNumber !== null && !withdrawn}
                >
                    <form id="owners-form" className={"needs-validation " + (this.state.wasValidated ? " was-validated" : "")} noValidate={true}>
                        <div className="form-row">
                            <EditableInputGroup
                                label="Sale Date:"
                                name="saleDate"
                                type="text"
                                className="col-md-12 col-lg-6"
                                inputClassName="not-required"
                                isRequired={false}
                                editable={false}
                                editableValue={(horseForSale.saleDate && horseForSale.saleDate.date) || ""}
                                nonEditableValue={(horseForSale.saleDate && horseForSale.saleDate.date) || ""}
                            />
                            <EditableInputGroup
                                label="Final Bid Amount:"
                                name="finalBidAmount"
                                type="text"
                                isRequired={true}
                                className="col-md-12 col-lg-6"
                                inputClassName="required"
                                editable={this.state.isEditable && !withdrawn}
                                editableValue={this.state.finalBidAmount}
                                nonEditableValue={
                                    (horseForSale.outcome &&
                                        (horseForSale.outcome as HammerFallOutcome).finalBidAmount &&
                                        NumberToMonetaryStringFormat((horseForSale.outcome as HammerFallOutcome).finalBidAmount)) ||
                                    ""
                                }
                                onChange={e => {
                                    const finalBidAmount = e.target.value;
                                    this.setState({ finalBidAmount });
                                }}
                                icon={<FontAwesomeIcon icon={faDollarSign} />}
                            />
                        </div>
                        <FinalBidAmountWarning finalBidAmount={parseFloat(this.state.finalBidAmount.replace(",", ""))} />
                        {!withdrawn && this.provisionallySoldSector()}
                    </form>
                </EditableCard>
                <AttachmentLogPanel
                    title={pageTitle}
                    attachmentLog={horseForSale.attachments.hammerFallFiles}
                    onAddAttachment={horseForSaleStore.addAttachment.bind(horseForSaleStore)}
                    onDeleteAttachment={horseForSaleStore.deleteAttachment.bind(horseForSaleStore)}
                    isEditable={EditableAcknowledgement.isAnEditableAttachment(horseForSale.state)}
                />
            </>
        );
    }

    private provisionallySoldSector() {
        const horseForSale = this.props.horseForSaleStore!.horseForSale!;
        const outcome = horseForSale.outcome;

        // We assume the following scheme to make our life easier
        return (
            <EditableSubCard label="Provisionally Sold To">
                <div className="form-row">
                    <div className="form-group col-sm-12">
                        <label htmlFor="winningBidder">
                            Name: <span className="text-danger">*</span>
                        </label>
                        <WhenEditable>
                            <CustomerSelect
                                customerEndpoints={this.props.customerEndpoints!}
                                include="BOTH"
                                showCreate={true}
                                value={
                                    (this.state.isEditable ? this.state.winningBidder : outcome && (outcome as HammerFallOutcome).winningBidder) ||
                                    null
                                }
                                onChange={(customer: SimpleCustomer) => {
                                    this.setState({ winningBidder: customer });
                                }}
                                onCreate={(customer: SimpleCustomer) => {
                                    this.setState({ winningBidder: customer });
                                }}
                                isInvalid={this.state.wasValidated && this.state.winningBidder === null}
                                noRedirect={true}
                                isDisabledLink={!this.state.isEditable}
                            />
                        </WhenEditable>
                        <WhenNotEditable>
                            {!(outcome && (outcome as HammerFallOutcome).winningBidder) && <div className="text-muted"> - Not Specified - </div>}
                        </WhenNotEditable>
                        {outcome && (outcome as HammerFallOutcome).winningBidder && (
                            <CustomerLink
                                customer={(this.state.isEditable ? this.state.winningBidder : (outcome as HammerFallOutcome).winningBidder) || null}
                                className="btn btn-info btn-sm btn-block py-2 mt-2"
                                includeAddress={true}
                                includePhone={true}
                                includeEmail={true}
                                formOfIdentification={true}
                                identificationNumber={true}
                                icons={true}
                                targetBlank={true}
                            />
                        )}
                    </div>
                </div>
            </EditableSubCard>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/horses-for-sale/AcknowledgementComponent.tsx